import {Injectable} from "@angular/core";
import {HttpClient, HttpContext, HttpHeaders} from "@angular/common/http";
import {firstValueFrom} from "rxjs";
import {RETRY_ON_AUTH_FAILURE} from './authentication/auth.interceptor';
import {environment} from '../../../environments/environment';

@Injectable({providedIn: "root"})
export class HttpRequestService {
  allData: any = [];

  constructor(private http: HttpClient) {
  }


  /**
   * @param urlPath                   Target URL
   * @param headers                   Optional headers (HttpHeaders is immutable!)
   * @param method                    'get' | 'post' | 'put' | 'delete'
   * @param requestBody               JSON payload for post/put
   * @param formData                  FormData payload for file uploads
   * @param context                   HttpContext to pass flags (e.g., RETRY_ON_AUTH_FAILURE)
   * @param retryOnAuthFailure        Convenience flag to set RETRY_ON_AUTH_FAILURE in context
   */
  async executeRequest(
    urlPath: string,
    headers = new HttpHeaders(),
    method: "get" | "post" | "put" | "delete",
    requestBody: Object = {},
    formData?: FormData | Record<string, any>,
    context?: HttpContext,
    retryOnAuthFailure: boolean = false,
  ): Promise<any> {
    // Ensure we have a context instance if the caller set the flag
    if (retryOnAuthFailure) {
      context = (context ?? new HttpContext()).set(RETRY_ON_AUTH_FAILURE, true);
    }
    let response;
    switch (method) {
      case "get":
        response = await firstValueFrom(
          this.getHttpRequest(urlPath, headers, context),
        );
        break;
      case "post":
        // console.log('executeRequest:', [requestBody, 'post']);
        if (formData && formData instanceof FormData && formData.keys().next()) {
          // DO NOT set Content-Type for FormData; browser will set boundary automatically.
          response = await firstValueFrom(
            this.postHttpRequest(urlPath, formData, headers, context)
          );
        } else {
          response = await firstValueFrom(
            this.postHttpRequest(urlPath, requestBody, headers, context)
          );
        }
        break;

      case "put":
        headers = headers.set("Content-Type", "application/json");
        response = await firstValueFrom(
          this.putHttpRequest(urlPath, requestBody, headers, context),
        );
        break;
      case "delete":
        response = await firstValueFrom(
          this.deleteHttpRequest(urlPath, headers, context),
        );
        break;
      default:
        throw new Error("HTTP method not supported: " + method);
    }
    this.allData = response;
    return this.allData;
  }

  private buildUrl(urlPath: string): string {
    // If urlPath is absolute, leave it; otherwise prefix with HTTPS base
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    // Ensure single slash join
    const base = environment.API_BASE_URL.replace(/\/+$/, '');
    const path = urlPath.replace(/^\/+/, '');
    return `${base}/${path}`;
  }

  getHttpRequest(
    url: string,
    headers = new HttpHeaders(),
    context?: HttpContext
  ) {
    return this.http.get(this.buildUrl(url), {headers, context});
  }

  postHttpRequest(
    url: string,
    requestBody: Object,
    headers = new HttpHeaders(),
    context: HttpContext | undefined,
  ) {
    return this.http.post(this.buildUrl(url), requestBody, {headers, context});
  }

  putHttpRequest(
    url: string, requestBody: Object, headers = new HttpHeaders(), context: HttpContext | undefined,
  ) {
    return this.http.put(this.buildUrl(url), requestBody, {headers, context});
  }

  deleteHttpRequest(url: string, headers = new HttpHeaders(), context: HttpContext | undefined) {
    return this.http.delete(this.buildUrl(url), {headers, context});
  }
}
